<template>
  <div>
    <div
      class="space-y-20 rounded-b-xl bg-cream pb-6 lg:rounded-b-4xl"
    >
      <div class="relative py-10 lg:pt-40">
        <div class="absolute inset-x-0 bottom-24 top-0 rounded-2xl bg-dynamic lg:inset-x-6 lg:top-6" />
        <div class="container relative space-y-10">
          <div class="rounded-2xl bg-white px-6 py-10 lg:grid lg:grid-cols-5 lg:gap-12 lg:p-20">
            <div class="hidden lg:col-span-3 lg:block">
              <div>
                <NuxtPicture
                  v-if="employer.active_image"
                  :img-attrs="{class: 'mask'}"
                  :src="employer.active_image.url"
                  :alt="employer.alias || employer.title"
                />
              </div>
            </div>
            <div class="col-span-2 space-y-8">
              <div class="max-w-xs">
                <NuxtPicture
                  v-if="employer.logo"
                  :src="employer.logo.url"
                  :alt="employer.alias || employer.title"
                />
              </div>
              <SofieText
                v-if="employer.intro"
                :value="employer.intro"
              />
              <ul class="space-y-8">
                <li v-if="employer.street && employer.zip">
                  📍 {{ employer.street }}, {{ employer.zip }}
                </li>
                <li
                  v-if="employer.email"
                  class="truncate whitespace-nowrap"
                >
                  📨 {{ employer.email }}
                </li>
                <li v-if="employer.phone">
                  📞 {{ employer.phone }}
                </li>
              </ul>
            </div>
          </div>
          <div
            v-if="employer.usps?.length"
            class="grid gap-4 lg:grid-cols-3"
          >
            <BaseCard
              v-for="usp in employer.usps.slice(0, 3)"
              :key="usp.id"
              class="card-base space-y-4"
            >
              <div class="text-dynamic">
                {{ usp.title }}
              </div>
              <div>{{ usp.text }}</div>
            </BaseCard>
          </div>
        </div>
      </div>
      <div
        v-if="employer.text"
        class="container"
      >
        <SofieText
          class="card-base prose max-w-none gap-8 p-10 lg:columns-2"
          :value="employer.text"
        />
      </div>
      <div>
        <VacanciesVacant :vacancies="employer.vacancies" />
      </div>
      <div
        v-if="employer.references.length"
        class="container"
      >
        <BaseChipTitle
          title="Wat zeggen je"
          chip="toekomstige collega's"
          center
          bg-color="bg-dynamic"
        />
        <ReferenceCarousel :references="employer?.references || []" />
      </div>
      <dynamic-style :color="employer.color_code ?? '#7270C2'" />
    </div>
    <div
      v-if="employer?.questions"
      class="bg-white pt-20"
    >
      <div class="container">
        <div class="flex gap-8 rounded-2xl bg-navy/10 p-8 max-md:flex-col lg:p-20">
          <div class="basis-1/2">
            <BaseChipTitle
              title="Veelgestelde"
              chip="vragen"
              bg-color="bg-dynamic"
              single-line
            />
            <div class="space-y-4">
              <QuestionTile
                v-for="question in employer.questions"
                :key="question.id"
                :question="question"
              />
            </div>
          </div>
          <RecruiterTile
            v-if="employer.recruiter"
            :recruiter="employer.recruiter"
            large
            class="grow"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {EmployerQuery} from '~/graphql/graphql';
import {EmployerDocument} from '~/graphql/graphql';

definePageMeta({
  header: {
    absolute: true,
    transparent: true,
    light: true,
  },
  bg: 'bg-white',
});

const route = useRoute();

const {data} = await useAsyncQuery<EmployerQuery>(EmployerDocument, {
  slug: route.path.split('/').pop(),
});

if (!data.value?.employer) {
  throw createError({statusCode: 404});
}

const employer = computed(() => data.value.employer!);
</script>
